import { useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { locationsActions } from '@behavior/locations';
import { Mode } from '@src/constants';
import { LocationDTO } from '@behavior/inventories/dto/LocationDTO';

// Palette mirrored from ActionCard.tsx / ActionsDetailsContainer.tsx.
const INK = '#111418';
const TEXT = '#1f2328';
const TEXT_SECONDARY = '#5b6470';
const TEXT_MUTED = '#8b95a3';
const BORDER = '#e6e8ec';
const BORDER_HOVER = '#d4d8df';
const SURFACE = '#ffffff';
const SURFACE_SUBTLE = '#f7f8fa';
const ACCENT = '#0f766e';
const ACCENT_TINT = 'rgba(15, 118, 110, 0.10)';
const ERROR = '#dc2626';
const OOB_BG = '#b45309';

const MAX_LOCATIONS = 4;

// Keep MUI's substring matching, but rank options that *start* with the
// query above the rest — typing "29" surfaces "29/1-001" before "1/1-029".
// The sort is stable, so the backend's original order is kept within each
// group.
const filterLocations = (
  options: LocationDTO[],
  { inputValue }: { inputValue: string },
): LocationDTO[] => {
  const query = inputValue.trim().toLowerCase();
  if (!query) {
    return options;
  }
  return options
    .filter(option => option.locationInfo.toLowerCase().includes(query))
    .sort((a, b) => {
      const aRank = a.locationInfo.toLowerCase().startsWith(query) ? 0 : 1;
      const bRank = b.locationInfo.toLowerCase().startsWith(query) ? 0 : 1;
      return aRank - bRank;
    });
};

type Props = {
  locationList?: LocationDTO[];
  getLocationList?: () => void;
  selectedLocations: LocationDTO[];
  setSelectedLocations?: (selectedLocations: LocationDTO[]) => void;
  outOfBuilding: boolean;
  setOutOfBuilding?: (outOfBuilding: boolean) => void;
  disabled?: boolean;
  mode?: Mode;
};

const ActivityLocation = ({
  getLocationList,
  locationList,
  selectedLocations,
  setSelectedLocations,
  outOfBuilding,
  setOutOfBuilding,
  disabled,
  mode,
}: Props) => {
  const { t } = useTranslation();
  const {
    register,
    clearErrors,
    formState: { errors },
    setValue,
  } = useFormContext();

  useEffect(() => {
    if (mode !== Mode.VIEW && locationList && locationList.length === 0 && getLocationList) {
      getLocationList();
    }
  }, [mode]);

  useEffect(() => {
    setValue('location', selectedLocations);
  }, [selectedLocations]);

  register('location', {
    validate: {
      required: (value: LocationDTO[]) =>
        (value && value.length > 0) || t('activity.form.error.location.required').toString(),
    },
  });

  const onLocationSelect = (value: LocationDTO[]) => {
    if (setSelectedLocations) {
      setSelectedLocations(value.slice(0, MAX_LOCATIONS));
    }
    clearErrors('location');
  };

  const onOutOfBuilding = () => {
    if (setOutOfBuilding) {
      setOutOfBuilding(!outOfBuilding);
    }
  };

  const locationError = errors?.location?.message?.toString();
  const hasLocations = Boolean(selectedLocations && selectedLocations.length > 0);

  return (
    <Box
      sx={{
        bgcolor: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: '10px',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${BORDER}` }}>
        <Typography
          component="div"
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: TEXT_MUTED,
          }}
        >
          {t('activity.details.page.location.container.title')}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {disabled ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            {hasLocations ? (
              selectedLocations.map(loc => (
                <Box
                  key={loc.id}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 24,
                    px: 1.125,
                    borderRadius: '6px',
                    bgcolor: SURFACE_SUBTLE,
                    border: `1px solid ${BORDER}`,
                    fontSize: 12,
                    fontWeight: 500,
                    color: TEXT,
                  }}
                >
                  {loc.locationInfo}
                </Box>
              ))
            ) : (
              <Typography sx={{ fontSize: 12.5, color: TEXT_MUTED, fontStyle: 'italic' }}>
                {t('activity.details.page.location.container.empty')}
              </Typography>
            )}
            {outOfBuilding && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  height: 24,
                  px: 1,
                  borderRadius: '6px',
                  bgcolor: OOB_BG,
                  color: '#ffffff',
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                }}
              >
                <WarningAmberRoundedIcon sx={{ fontSize: 12 }} />
                {t('action.list.outsideBuilding')}
              </Box>
            )}
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, mb: 0.75 }}>
              {t('activity.details.page.location.container.location.input.label')}
            </Typography>
            <Autocomplete
              multiple
              disableClearable
              filterSelectedOptions
              filterOptions={filterLocations}
              options={locationList ?? []}
              value={selectedLocations ?? []}
              getOptionLabel={option => option.locationInfo}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              getOptionDisabled={() => (selectedLocations?.length ?? 0) >= MAX_LOCATIONS}
              onChange={(_, value) => onLocationSelect(value as LocationDTO[])}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.locationInfo}
                    size="small"
                    sx={{
                      height: 24,
                      borderRadius: '6px',
                      bgcolor: ACCENT_TINT,
                      color: ACCENT,
                      fontWeight: 600,
                      fontSize: 12,
                      '& .MuiChip-deleteIcon': {
                        color: ACCENT,
                        fontSize: 16,
                        '&:hover': { color: INK },
                      },
                    }}
                  />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={
                    (selectedLocations?.length ?? 0) === 0
                      ? t(
                          'activity.details.page.location.container.location.input.placeholder',
                        ).toString()
                      : ''
                  }
                  error={Boolean(locationError)}
                  helperText={locationError}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': { borderColor: BORDER_HOVER },
                      '&:hover fieldset': { borderColor: TEXT_MUTED },
                      '&.Mui-focused fieldset': { borderColor: ACCENT },
                    },
                    // This card renders inside a semantic-ui <Form>, whose
                    // `.ui.form input[type=text]` rule outranks MUI and would
                    // force the Autocomplete's native input to width:100% with
                    // its own border — pushing it onto a line below the chips.
                    // Reset it so the input flows inline with the tags.
                    '& .MuiAutocomplete-input': {
                      width: '0 !important',
                      minWidth: '30px !important',
                      flexGrow: 1,
                      border: 'none !important',
                      borderRadius: '0 !important',
                      boxShadow: 'none !important',
                      padding: '4px 6px !important',
                      background: 'transparent !important',
                    },
                    '& .MuiFormHelperText-root': { color: ERROR, marginLeft: 0 },
                  }}
                />
              )}
            />
            <Typography sx={{ fontSize: 11.5, color: TEXT_MUTED, mt: 0.75 }}>
              {t('activity.details.page.location.container.hint')}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={outOfBuilding}
                  onChange={onOutOfBuilding}
                  disabled={disabled}
                  sx={{ color: BORDER_HOVER, '&.Mui-checked': { color: ACCENT } }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: TEXT }}>
                  {t('activity.details.page.location.container.is.out.of.building.input.label')}
                </Typography>
              }
              sx={{ mt: 1, ml: -0.5 }}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

const mapStateToProps = (state: any) => ({
  ...state.locations,
});

const mapDispatchToProps = (dispatch: any) => ({
  getLocationList: () => dispatch(locationsActions.getLocationAutocompleteList(false)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ActivityLocation);
